import React, { useCallback, useEffect, useState } from 'react';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import Button from '../../shared/components/ui/Button';
import ZohoIntegration from '../components/forms/ZohoIntegration';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  getAllCompanies,
  addZohoIntegration,
  getZohoOAuthUrl,
  exchangeZohoOAuthToken,
  getZohoStatusByCompany
} from '../services/api/implementation/impl';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';

const ZohoCompanies = () => {
  const userId = Cookies.get('userId');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [companies, setCompanies] = useState([]);
  const [connectedMap, setConnectedMap] = useState({});
  const [statusLoadingMap, setStatusLoadingMap] = useState({});
  const [isConfigureModalOpen, setIsConfigureModalOpen] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [selectedCompanyZohoData, setSelectedCompanyZohoData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { collapsed } = useSidebar();

  const code = searchParams.get('code');
  const state = searchParams.get('state');

  useEffect(() => {
    mixpanel.track('Zoho Companies Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  const { data: companiesData, isPending: isCompaniesPending } = useQuery({
    queryKey: ['companies'],
    queryFn: getAllCompanies
  });

  const checkZohoStatus = useCallback(async (companyId) => {
    setStatusLoadingMap((prev) => ({ ...prev, [companyId]: true }));
    try {
      const data = await getZohoStatusByCompany(companyId);
      if (data && data.access_token && data.refresh_token) {
        setConnectedMap((prev) => ({ ...prev, [companyId]: { connected: true, data } }));
      } else if (data) {
        setConnectedMap((prev) => ({ ...prev, [companyId]: { connected: false, data } }));
      }
    } catch {
      setConnectedMap((prev) => ({ ...prev, [companyId]: { connected: false, data: null } }));
    } finally {
      setStatusLoadingMap((prev) => ({ ...prev, [companyId]: false }));
    }
  }, []);

  useEffect(() => {
    if (companiesData) {
      setCompanies(companiesData);
      companiesData.forEach((company) => {
        checkZohoStatus(company.id);
      });
    }
  }, [companiesData, checkZohoStatus]);

  // Step 3: Handle OAuth redirect — exchange code for tokens
  useEffect(() => {
    if (code && state) {
      console.log('Zoho OAuth redirect URL:', window.location.href);
      console.log('Zoho OAuth code:', code);
      console.log('Zoho OAuth state (company_id):', state);

      const companyId = parseInt(state, 10);
      handleExchangeToken({ code, company_id: companyId });

      const newParams = new URLSearchParams(searchParams);
      newParams.delete('code');
      newParams.delete('state');
      newParams.delete('location');
      newParams.delete('accounts-server');
      setSearchParams(newParams);
    }
  }, [code, state]);

  const { mutate: handleExchangeToken } = useMutation({
    mutationFn: (payload) =>
      {
      console.log('Zoho token exchange payload:', payload);
      exchangeZohoOAuthToken(payload)
      },
    onSuccess: (data) => {
      console.log('Zoho token exchange response:', data);
      toast.success('Zoho connected successfully!');
      if (data?.company_id) {
        setConnectedMap((prev) => ({
          ...prev,
          [data.company_id]: { connected: true, data }
        }));
      }
      window.history.replaceState({}, '', window.location.pathname);
    },
    onError: (err) => {
      console.error('Error exchanging Zoho OAuth token', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error connecting to Zoho');
    }
  });

  // Step 1: Save credentials only
  const handleSaveCredentials = async (formData) => {
    setIsSubmitting(true);
    try {
      await addZohoIntegration(formData);
      toast.success('Credentials saved successfully');
      setIsConfigureModalOpen(false);
      await checkZohoStatus(formData.company_id);
    } catch (err) {
      console.error('Error saving Zoho credentials', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error saving credentials');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Step 2: Trigger OAuth redirect (separate button)
  const handleOAuthConnect = async (companyId) => {
    setIsSubmitting(true);
    try {
      const oauthData = await getZohoOAuthUrl(companyId);
      console.log('OAuth Data=====>', oauthData);
      if (oauthData?.url) {
        window.location.href = oauthData.url;
      } else {
        toast.error('Failed to get Zoho OAuth URL');
      }
    } catch (err) {
      console.error('Error getting Zoho OAuth URL', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error connecting to Zoho');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfigureClick = (companyId) => {
    setSelectedCompanyId(companyId);
    const existing = connectedMap[companyId]?.data || null;
    setSelectedCompanyZohoData(existing);
    setIsConfigureModalOpen(true);
  };

  return (
    <Layout>
      <div
        className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
          collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
        }`}>
        <Header
          backArrow={true}
          onBack={() => navigate('/builder/integration')}
          data={{ name: 'Integrations > Zoho' }}
        />
      </div>

      <div className={`transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''}`}>
        <div className="mt-[4.5rem] w-full h-full bg-gray-100 p-6">
          {isCompaniesPending ? (
            <div className="flex min-h-[40vh] w-full items-center justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#346C75] border-t-transparent" />
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-6">
              {companies.map((company) => {
                const status = connectedMap[company.id];
                const isZohoStatusLoading = statusLoadingMap[company.id];
                const isConnected = status?.connected === true;
                const hasCredentials = status?.data && !isConnected;
                const logoUrl = Array.isArray(company.company_logo)
                  ? company.company_logo[0]?.url
                  : company.company_logo?.url || company.company_logo || null;

                return (
                  <div key={company.id} className="bg-white shadow-md p-6 rounded-lg flex flex-col">
                    {logoUrl ? (
                      <img
                        className="border h-[6rem] object-contain mb-2"
                        src={logoUrl}
                        alt="Company Logo"
                      />
                    ) : null}
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="font-bold text-lg">{company.company_name}</h3>
                      {isConnected && (
                        <div className="text-[#418b7a] flex gap-[2px] justify-center items-center text-sm font-medium">
                          <span className="border bg-[#418b7a] rounded-full p-[0.20rem] text-[#418b7a] mt-[2px]"></span>
                          <p>Connected</p>
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-4 text-justify flex-grow">
                      Connect this company to Zoho Books to sync invoices, contacts, and payments
                      automatically.
                    </p>
                    <div className="w-full flex gap-3 justify-end mt-auto">
                      {isZohoStatusLoading ? (
                        <Button isLoading disabled displayType="primary">
                          Configure
                        </Button>
                      ) : isConnected ? (
                        <Button displayType="primary" disabled>
                          Connected
                        </Button>
                      ) : hasCredentials ? (
                        <Button
                          onClick={() => handleOAuthConnect(company.id)}
                          disabled={isSubmitting}>
                          Connect
                        </Button>
                      ) : (
                        <Button
                          onClick={() => handleConfigureClick(company.id)}
                          disabled={isSubmitting}>
                          Configure
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {isConfigureModalOpen && (
        <ZohoIntegration
          isSubmitting={isSubmitting}
          onClose={() => setIsConfigureModalOpen(false)}
          onConnect={handleSaveCredentials}
          defaultValues={selectedCompanyZohoData}
          companyId={selectedCompanyId}
        />
      )}
    </Layout>
  );
};

export default ZohoCompanies;
